// Pure model + helpers for the shared inspector primitives (ADR-0129).
// Kept framework-free so the ordering, active-slot, clamp, and storage-key
// logic is unit-testable without a React DOM harness (the repo tests pure
// logic via node:test only).

/**
 * The fixed five-slot inspector tab spine, in canonical display order.
 * Info leads (ADR-0129, owner decision 2026-07-12): the "what/where this node
 * is" identity + source config reads before the "how it's configured" Setup —
 * and it's where an inspector opens by default when no slot is persisted.
 */
export const INSPECTOR_SLOT_ORDER = ["info", "setup", "data", "ai", "runs"] as const;

export type InspectorSlot = (typeof INSPECTOR_SLOT_ORDER)[number];

export type InspectorTabDef<TContent> = {
  slot: InspectorSlot;
  /** Optional label override; defaults to the canonical slot title. */
  label?: string;
  content: TContent;
  /** Optional count/dot rendered next to the label (e.g. validation issues on Info). */
  badge?: TContent;
  /**
   * Fill mode: the panel fills the frame and does not scroll — its content
   * manages its own scroll (e.g. a chat message list that flexes with the
   * composer pinned to the bottom). Default (false) = the panel scrolls, for
   * forms/previews.
   */
  fill?: boolean;
};

const SLOT_LABEL: Record<InspectorSlot, string> = {
  setup: "Setup",
  data: "Data",
  ai: "AI",
  runs: "Runs",
  info: "Info",
};

export function slotLabel(slot: InspectorSlot, override?: string): string {
  return override ?? SLOT_LABEL[slot];
}

/**
 * Sort caller-provided tabs into canonical spine order, dropping any slot not
 * in the taxonomy and collapsing gaps. If a slot appears more than once the
 * first occurrence wins (deterministic).
 */
export function orderInspectorTabs<T>(tabs: ReadonlyArray<InspectorTabDef<T>>): InspectorTabDef<T>[] {
  const out: InspectorTabDef<T>[] = [];
  for (const slot of INSPECTOR_SLOT_ORDER) {
    const found = tabs.find((t) => t.slot === slot);
    if (found) out.push(found);
  }
  return out;
}

/**
 * Resolve which slot should be active given the persisted preference and the
 * slots actually available for this inspector. Precedence: a valid persisted
 * choice → the inspector's `preferred` default (if populated) → the first
 * available slot in canonical order. `preferred` lets a node open on Setup
 * (configure-first) even though Info leads the spine.
 */
export function resolveActiveSlot(
  persisted: string | null | undefined,
  available: ReadonlyArray<InspectorSlot>,
  preferred?: InspectorSlot,
): InspectorSlot | null {
  if (available.length === 0) return null;
  if (persisted && (available as ReadonlyArray<string>).includes(persisted)) {
    return persisted as InspectorSlot;
  }
  if (preferred && available.includes(preferred)) return preferred;
  return available[0];
}

/** Clamp a width to [min, max]; NaN resolves to min (safe default). */
export function clampInspectorWidth(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  const hi = Math.max(min, max);
  return Math.max(min, Math.min(hi, value));
}

/** Resolve the effective max width, supporting a viewport-relative function. */
export function resolveMaxWidth(
  max: number | ((viewportWidth: number) => number),
  viewportWidth: number,
): number {
  return typeof max === "function" ? max(viewportWidth) : max;
}

/** localStorage key for a flyout's persisted width, scoped per surface + panel. */
export function widthStorageKey(surfaceId: string, panelId: string): string {
  return `atlas.inspector.${surfaceId}.${panelId}.width`;
}

/** localStorage key for a tab group's last-active slot, scoped per surface + group. */
export function tabStorageKey(surfaceId: string, tabGroupId: string): string {
  return `atlas.inspector.${surfaceId}.${tabGroupId}.tab`;
}
